import { min, remove, round } from 'lodash-es';
import { Container, Graphics, Point } from 'pixi.js-legacy';
import Constants from '../constants';
import { distanceBetween, intersects } from '../helpers';
import { DragManager, DragOptions, ScalableTextManager, ScalableTextOptions, SelectManager, TooltipManager, TooltipOptions, TooltipPosition, } from '../managers';
export class CutoutOptions {
    constructor() {
        this.id = '';
        this.position = new Point(0, 0);
        this.xDim = 0;
        this.yDim = 0;
    }
}
export class Cutout extends Container {
    constructor(options = new CutoutOptions()) {
        super();
        this.background = new Graphics();
        this.snappingDistance = 20;
        this.name = 'Cutout';
        this.id = options.id;
        this.tier = options.tier;
        this.width = options.xDim;
        this.height = options.yDim;
        this.scaledXDim = options.xDim;
        this.scaledYDim = options.yDim;
        this.dimensions = new Container();
        if ((this.scaledXDim / Constants.scalingFactor) *
            (this.scaledYDim / Constants.scalingFactor) <
            0.4) {
            this.createText = false;
        }
        else {
            this.createText = true;
        }
        this.draw(options.position, options.xDim, options.yDim, 1, 0);
        this.addChild(this.background);
        const dragOptions = new DragOptions();
        dragOptions.bringToFront = true;
        dragOptions.priority = 1;
        dragOptions.dragStart = this.dragStart;
        dragOptions.dragStop = this.dragStop;
        DragManager.getInstance().addDraggable(this, dragOptions);
        SelectManager.getInstance().addSelectable(this, null);
        this.on('mouseover', this.onHover).on('mouseout', this.onStopHover);
        if (!this.createText) {
            const tooltipOptions = new TooltipOptions();
            tooltipOptions.text =
                'Cutout \n' +
                    `XDim: ${this.scaledXDim / Constants.scalingFactor}, ` +
                    `YDim: ${this.scaledYDim / Constants.scalingFactor}`;
            tooltipOptions.position = TooltipPosition.Top;
            tooltipOptions.tierOffset = this.tier.position;
            this.tooltip = TooltipManager.getInstance().addTooltip(this, tooltipOptions);
        }
    }
    setShouldRenderOutline(val) {
        this._shouldRenderOutline = val;
        this.draw(this.position, this.scaledXDim, this.scaledYDim, 1, 0);
    }
    getShouldRenderOutline() {
        return this._shouldRenderOutline;
    }
    draw(pos, xDim, yDim, alpha, lineWidth, lineColor = 0x000000, lineAlpha = 1) {
        if (this._shouldRenderOutline) {
            lineAlpha = 1;
            lineWidth = 1;
            alpha = 0.6;
        }
        this.background.clear();
        this.background.lineStyle(lineWidth, lineColor, lineAlpha);
        this.background.beginFill(Constants.rendererBackgroundColour, alpha);
        this.background.drawRect(0, 0, xDim, yDim);
        this.background.endFill();
        this.x = pos.x;
        this.y = pos.y;
        if (this.tooltip) {
            this.tooltip.update();
        }
        else {
            if (this.createText) {
                this.removeChild(this.dimensions);
                this.dimensions = new Container();
                this.addChild(this.dimensions);
                this.createOriginText();
                this.createXDimText();
                this.createYDimText();
            }
        }
    }
    createOriginText() {
        const options = new ScalableTextOptions();
        options.text =
            `(${round(this.position.x / Constants.scalingFactor, 3)}, ` +
                `${round(this.position.y / Constants.scalingFactor, 3)})`;
        options.x = 0;
        options.y = -30;
        options.anchor = new Point(0.5, 0.5);
        options.size = 40;
        ScalableTextManager.getInstance().createText(this.dimensions, options);
    }
    createXDimText() {
        const options = new ScalableTextOptions();
        options.text = round(this.scaledXDim / Constants.scalingFactor, 3) + 'm';
        options.x = this.scaledXDim / 2;
        options.y = this.scaledYDim - 20;
        options.anchor = new Point(0.5, 0.5);
        options.size = 40;
        ScalableTextManager.getInstance().createText(this.dimensions, options);
    }
    createYDimText() {
        const options = new ScalableTextOptions();
        options.text = round(this.scaledYDim / Constants.scalingFactor, 3) + 'm';
        options.x = 20;
        options.y = this.scaledYDim / 2;
        options.anchor = new Point(0.5, 0.5);
        options.rotation = 4.71239;
        options.size = 40;
        ScalableTextManager.getInstance().createText(this.dimensions, options);
    }
    onHover() {
        this.draw(this.position, this.scaledXDim, this.scaledYDim, 0.5, 4);
    }
    onStopHover() {
        this.draw(this.position, this.scaledXDim, this.scaledYDim, 1, 0);
    }
    dragStart() {
        this.tier.cutouts.removeChild(this);
        this.tier.foreground.addChild(this);
    }
    dragStop() {
        this.tier.foreground.removeChild(this);
        this.tier.cutouts.addChild(this);
        this.keepInTier();
        this.checkCollisionsAndSnapToOthers();
        this.draw(this.position, this.scaledXDim, this.scaledYDim, 1, 0);
    }
    checkCollisionsAndSnapToOthers() {
        this.collideWithCutouts();
        this.snapToNearEdges();
        this.snapToCloseCutouts();
    }
    asRect() {
        return {
            x: this.position.x,
            y: this.position.y,
            width: this.scaledXDim,
            height: this.scaledYDim,
        };
    }
    snapToNearEdges() {
        if (!this.allowEdgeSnapping) {
            return;
        }
        const decking = this.tier.decking;
        const snapDistance = Constants.snapToEdgeDistance;
        if (this.position.x <= decking.position.x + snapDistance) {
            this.position.x = decking.position.x;
        }
        if (this.position.x + this.scaledXDim >=
            decking.position.x + decking.width - snapDistance) {
            this.position.x = decking.position.x + decking.width - this.scaledXDim;
        }
        if (this.position.y <= decking.position.y + snapDistance) {
            this.position.y = decking.position.y;
        }
        if (this.position.y + this.scaledYDim >=
            decking.position.y + decking.height - snapDistance) {
            this.position.y = decking.position.y + decking.height - this.scaledYDim;
        }
    }
    keepInTier() {
        const decking = this.tier.decking;
        if (this.position.x < decking.position.x) {
            this.position.x = decking.position.x;
        }
        if (this.position.y < decking.position.y) {
            this.position.y = decking.position.y;
        }
        if (this.position.x + this.scaledXDim >
            decking.position.x + decking.width) {
            this.position.x = decking.position.x + decking.width - this.scaledXDim;
        }
        if (this.position.y + this.scaledYDim >
            decking.position.y + decking.height) {
            this.position.y = decking.position.y + decking.height - this.scaledYDim;
        }
    }
    getTierCutouts() {
        return this.parent.children.filter((child) => {
            return child.name === 'Cutout' && child !== this;
        });
    }
    getIntersectingCutouts() {
        return this.getTierCutouts().filter((cutout) => {
            return this.intersectsWithOther(cutout);
        });
    }
    intersectsWithOther(cutout) {
        return intersects(this.asRect(), cutout.asRect());
    }
    collideWithCutouts() {
        if (!this.allowCollision) {
            return;
        }
        this.getIntersectingCutouts().forEach((cutout) => {
            this.snapToNearestSide(cutout);
        });
    }
    snapToCloseCutouts() {
        if (!this.allowCutoutSnapping) {
            return;
        }
        this.getTierCutouts().forEach((cutout) => {
            if (this.isCutoutToTheRight(this, cutout)) {
                this.position.x = cutout.position.x - this.scaledXDim;
            }
            if (this.isCutoutToTheLeft(this, cutout)) {
                this.position.x = cutout.position.x + cutout.scaledXDim;
            }
            if (this.isCutoutAbove(this, cutout)) {
                this.position.y = cutout.position.y - this.scaledYDim;
            }
            if (this.isCutoutBelow(this, cutout)) {
                this.position.y = cutout.position.y + cutout.scaledYDim;
            }
        });
    }
    snapToNearestSide(cutout) {
        const ourMidpoint = new Point(this.position.x + this.scaledYDim / 2, this.position.y + this.scaledYDim / 2);
        const centreLeftEdge = new Point(cutout.position.x, cutout.position.y + cutout.scaledYDim / 2);
        const centreRightEdge = new Point(cutout.position.x + cutout.scaledYDim, cutout.position.y + cutout.scaledYDim / 2);
        const centreTopEdge = new Point(cutout.position.x + cutout.scaledXDim / 2, cutout.position.y);
        const centreBottomEdge = new Point(cutout.position.x + cutout.scaledXDim / 2, cutout.position.y + cutout.scaledYDim);
        const distanceToLeftEdge = distanceBetween(ourMidpoint, centreLeftEdge);
        const distanceToRightEdge = distanceBetween(ourMidpoint, centreRightEdge);
        const distanceToTopEdge = distanceBetween(ourMidpoint, centreTopEdge);
        const distanceToBottomEdge = distanceBetween(ourMidpoint, centreBottomEdge);
        const all = [
            distanceToLeftEdge,
            distanceToRightEdge,
            distanceToTopEdge,
            distanceToBottomEdge,
        ];
        let smallest = min(all);
        let checks = 0;
        const maxChecks = 4;
        while (all.length > 0 || checks <= maxChecks) {
            if (distanceToLeftEdge === smallest) {
                if (cutout.position.x - this.scaledXDim >=
                    this.tier.decking.position.x) {
                    this.position.x = cutout.position.x - this.scaledXDim;
                    return true;
                }
                else {
                    remove(all, (a) => a === smallest);
                    smallest = min(all);
                }
            }
            if (distanceToRightEdge === smallest) {
                if (cutout.position.x + cutout.scaledXDim + this.scaledXDim <=
                    this.tier.decking.position.x + this.tier.decking.width) {
                    this.position.x = cutout.position.x + cutout.scaledXDim;
                    return true;
                }
                else {
                    remove(all, (a) => a === smallest);
                    smallest = min(all);
                }
            }
            if (distanceToTopEdge === smallest) {
                if (cutout.position.y - this.scaledYDim >=
                    this.tier.decking.position.y) {
                    this.position.y = cutout.position.y - this.scaledYDim;
                    return true;
                }
                else {
                    remove(all, (a) => a === smallest);
                    smallest = min(all);
                }
            }
            if (distanceToBottomEdge === smallest) {
                if (cutout.position.y + cutout.scaledYDim + this.scaledYDim <=
                    this.tier.decking.position.y + this.tier.decking.height) {
                    this.position.y = cutout.position.y + cutout.scaledYDim;
                    return true;
                }
                else {
                    remove(all, (a) => a === smallest);
                    smallest = min(all);
                }
            }
            checks++;
        }
        return false;
    }
    isCutoutToTheRight(cutout, other) {
        return (Math.abs(cutout.position.x + cutout.scaledXDim - other.position.x) <
            this.snappingDistance &&
            cutout.position.y + cutout.scaledYDim >= other.position.y &&
            cutout.position.y <= other.position.y + other.scaledYDim);
    }
    isCutoutToTheLeft(cutout, other) {
        return (Math.abs(cutout.position.x - (other.position.x + other.scaledXDim)) <
            this.snappingDistance &&
            cutout.position.y + cutout.scaledYDim >= other.position.y &&
            cutout.position.y <= other.position.y + other.scaledYDim);
    }
    isCutoutAbove(cutout, other) {
        return (Math.abs(cutout.position.y + cutout.scaledYDim - other.position.y) <
            this.snappingDistance &&
            cutout.position.x + cutout.scaledYDim >= other.position.x &&
            cutout.position.x <= other.position.x + other.scaledYDim);
    }
    isCutoutBelow(cutout, other) {
        return (Math.abs(cutout.position.y - (other.position.y + other.scaledYDim)) <
            this.snappingDistance &&
            cutout.position.x + cutout.scaledYDim >= other.position.x &&
            cutout.position.x <= other.position.x + other.scaledYDim);
    }
}
